import type { SxProps, Theme } from '@mui/material/styles'
import { alpha, useTheme } from '@mui/material/styles'
import { AppBar, Box, Skeleton, Stack, Toolbar, Typography } from '@mui/material'
import { HEADER } from '../../config-global'
import { bgBlur } from '../../utils/cssStyles'
import { useMenuBranding } from './MenuBrandingContext'
import { MenuHelpButton } from './MenuHelpButton'

type HeaderProps = {
    isOffset?: boolean
}

function BrandingMark({
    logoUrl,
    restaurantName,
    branchName,
}: {
    logoUrl: string | null
    restaurantName: string | null
    branchName: string | null
}) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1.75}
            sx={{ minWidth: 0, flex: 1, maxWidth: '100%', overflow: 'hidden' }}
        >
            {logoUrl ? (
                <Box
                    component="img"
                    src={logoUrl}
                    alt={restaurantName || 'Logo del restaurante'}
                    sx={{
                        display: 'block',
                        width: 'auto',
                        height: 'auto',
                        maxWidth: {
                            xs: 'clamp(64px, 28vw, 104px)',
                            sm: 128,
                            md: 144,
                        },
                        maxHeight: { xs: 44, md: 52 },
                        objectFit: 'contain',
                        flexShrink: 0,
                    }}
                />
            ) : (
                <Skeleton
                    variant="rounded"
                    sx={{
                        width: { xs: 64, md: 72 },
                        height: { xs: 44, md: 52 },
                        flexShrink: 0,
                    }}
                />
            )}
            <Stack
                spacing={0.15}
                sx={{
                    minWidth: 0,
                    flex: 1,
                    overflow: 'hidden',
                    textAlign: 'left',
                }}
            >
                {restaurantName ? (
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {restaurantName}
                    </Typography>
                ) : null}
                {branchName ? (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {branchName}
                    </Typography>
                ) : null}
            </Stack>
        </Stack>
    )
}

function BrandingSkeleton() {
    return (
        <Stack direction="row" alignItems="center" spacing={1.75} sx={{ minWidth: 0 }}>
            <Skeleton
                variant="rounded"
                sx={{
                    width: { xs: 64, md: 72 },
                    height: { xs: 44, md: 52 },
                    flexShrink: 0,
                }}
            />
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                <Skeleton variant="text" width={120} height={22} />
                <Skeleton variant="text" width={72} height={18} />
            </Stack>
        </Stack>
    )
}

export default function Header({ isOffset }: HeaderProps) {
    const theme = useTheme()
    const { isFound, logoUrl, restaurantName, branchName, cartaStatus } = useMenuBranding()

    const showBranding = Boolean(isFound || logoUrl || restaurantName || branchName)
    const showSkeleton = !showBranding && cartaStatus === 'loading'
    const elevatedHeader = showBranding || showSkeleton

    let minHeight = {
        xs: elevatedHeader ? 72 : HEADER.H_MOBILE,
        md: elevatedHeader ? 80 : HEADER.H_MAIN_DESKTOP,
    }
    if (isOffset) {
        minHeight = {
            xs: elevatedHeader ? 68 : HEADER.H_MOBILE,
            md: elevatedHeader ? 72 : HEADER.H_MAIN_DESKTOP - 16,
        }
    }

    const toolbarSx = {
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        px: { xs: 2, md: 3 },
        transition: theme.transitions.create(['min-height', 'background-color', 'padding'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.shorter,
        }),
        ...(isOffset
            ? bgBlur({ color: theme.palette.background.default, opacity: 0.92 })
            : {}),
        minHeight,
    } as SxProps<Theme>

    let leadingContent = null
    if (showBranding) {
        leadingContent = (
            <BrandingMark
                logoUrl={logoUrl}
                restaurantName={restaurantName}
                branchName={branchName}
            />
        )
    } else if (showSkeleton) {
        leadingContent = <BrandingSkeleton />
    }

    return (
        <AppBar
            id="app-bar"
            color="transparent"
            elevation={0}
            sx={{
                boxShadow: 'none',
                ...(elevatedHeader && {
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    bgcolor: alpha(theme.palette.background.default, isOffset ? 0.72 : 0.4),
                    backdropFilter: isOffset ? 'blur(12px)' : 'none',
                }),
            }}
        >
            <Toolbar id="app-toolbar" disableGutters sx={toolbarSx}>
                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center' }}>
                    {leadingContent}
                </Box>

                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                    <MenuHelpButton />
                </Stack>
            </Toolbar>
            {isOffset && !elevatedHeader ? (
                <Box
                    sx={{
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 24,
                        zIndex: -1,
                        m: 'auto',
                        borderRadius: '50%',
                        position: 'absolute',
                        width: 'calc(100% - 48px)',
                        boxShadow: (t) =>
                            (t as Theme & { customShadows: { z8: string } }).customShadows
                                .z8,
                    }}
                />
            ) : null}
        </AppBar>
    )
}
